import { ChevronRight } from "lucide-react"
import { getIconComponentWithFallback } from "../utils/iconMapping"
import { Link } from "react-router-dom"
import React from "react"

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/components/ui/sidebar"
import { NavMainItem } from "../Interface/NavBar"

export function NavMain({ items }: { items: NavMainItem[] }) {
  return (
    <SidebarGroup>
      <SidebarGroupLabel>Navigation</SidebarGroupLabel>
      <SidebarMenu>
        {items.map((item) => {
          const hasSubItems = item.items && item.items.length > 0
          
          if (hasSubItems) {
            return <CollapsibleNavItem key={item.title} item={item} />
          } else {
            return <SimpleNavItem key={item.title} item={item} />
          }
        })}
      </SidebarMenu>
    </SidebarGroup>
  )
}

// Component for rendering collapsible navigation items with sub-menus
function CollapsibleNavItem({ item }: { item: NavMainItem }) {
  return (
    <Collapsible
      asChild
      defaultOpen={item.isActive}
      className="group/collapsible"
    >
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          <SidebarMenuButton tooltip={item.title}>
            <NavItemIcon icon={item.icon} />
            <span>{item.title}</span>
            <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub>
            {item.items?.map((subItem: NavMainItem) => (
              <SidebarMenuSubItem key={subItem.title}>
                <SidebarMenuSubButton asChild>
                  <Link to={subItem.url}>
                    <span>{subItem.title}</span>
                  </Link>
                </SidebarMenuSubButton>
              </SidebarMenuSubItem>
            ))}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  )
}

// Component for rendering simple navigation items without sub-menus
function SimpleNavItem({ item }: { item: NavMainItem }) {
  if (item.openInNewWindow) {
    return <NewWindowNavItem item={item} />
  } else if (item.openInNewTab) {
    return <NewTabNavItem item={item} />
  } else {
    return <InternalNavItem item={item} />
  }
  
}

// Component for navigation items that open new windows
function NewWindowNavItem({ item }: { item: NavMainItem }) {
  const handleClick = () => {
    openWindowsForItem(item)
  }

  return (
    <SidebarMenuItem>
      <SidebarMenuButton onClick={handleClick} tooltip={item.title}>
        <NavItemIcon icon={item.icon} />
        <span>{item.title}</span>
      </SidebarMenuButton>
    </SidebarMenuItem>
  )
}

// Component for internal navigation items
function InternalNavItem({ item }: { item: NavMainItem }) {
  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild tooltip={item.title}>
        <InternalLink item={item} />
      </SidebarMenuButton>
    </SidebarMenuItem>
  )
}

// Component for navigation items that open new tabs
function NewTabNavItem({ item }: { item: NavMainItem }) {
  return (
    <SidebarMenuItem>
      <SidebarMenuButton onClick={() => window.open(item.url, '_blank')} tooltip={item.title}>
        <NavItemIcon icon={item.icon} />
        <span>{item.title}</span>
      </SidebarMenuButton>
    </SidebarMenuItem>
  )
}

// Utility function to handle opening windows
function openWindowsForItem(item: NavMainItem) {
  // Open the main window
  window.open(item.url, '_blank', 'width=1200,height=800,scrollbars=yes,resizable=yes')
  
  // Open any additional windows
  if (item.additionalWindows) {
    item.additionalWindows.forEach((additionalWindow, index) => {
      // Offset each additional window slightly so they don't overlap
      const offsetX = (index + 1) * 50
      const offsetY = (index + 1) * 50
      window.open(
        additionalWindow.url, 
        '_blank', 
        `width=1200,height=800,scrollbars=yes,resizable=yes,left=${offsetX},top=${offsetY}`
      )
    })
  }
}

// Component for rendering internal navigation links
const InternalLink = React.forwardRef<HTMLAnchorElement, { item: NavMainItem }>(
  ({ item, ...props }, ref) => {
    return (
      <Link ref={ref} to={item.url} {...props}>
        <NavItemIcon icon={item.icon} />
        <span>{item.title}</span>
      </Link>
    )
  }
)

InternalLink.displayName = "InternalLink"

// Component for rendering navigation item icons
function NavItemIcon({ icon }: { icon?: string | any }) {
  if (!icon) return null
  
  const IconComponent = typeof icon === 'string' 
    ? getIconComponentWithFallback(icon) 
    : icon
  
  return <IconComponent />
}
